import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Heart, Loader2 } from "lucide-react";

export default function Signup() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to={["super_admin", "employee"].includes(user.role) ? "/admin" : "/account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      setUser(data);
      nav("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF8F0] noise-bg px-6" data-testid="signup-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="w-10 h-10 rounded-full bg-[#C1272D] flex items-center justify-center text-white"><Heart size={20} fill="white" /></span>
          <span className="font-display text-2xl font-bold text-[#1F4D2E]">Refugium Ethiopia</span>
        </Link>
        <div className="bg-white border border-[#1F4D2E]/10 rounded-xl shadow-sm p-8">
          <h1 className="font-display text-3xl font-bold text-[#1F4D2E] mb-1">Create your donor account</h1>
          <p className="text-sm text-[#4F6B54] mb-6">Join our community, follow campaign updates, and manage your giving profile.</p>
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-md px-4 py-3 mb-4" data-testid="signup-error">{error}</div>}
          <form onSubmit={submit}>
            <label className="block text-sm font-medium text-[#1F4D2E] mb-1.5">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name"
              className="w-full border border-[#1F4D2E]/15 rounded-md px-4 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-[#C1272D]" placeholder="Your name" />
            <label className="block text-sm font-medium text-[#1F4D2E] mb-1.5">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email"
              className="w-full border border-[#1F4D2E]/15 rounded-md px-4 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-[#C1272D]" placeholder="you@email.com" />
            <label className="block text-sm font-medium text-[#1F4D2E] mb-1.5">Password</label>
            <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password"
              className="w-full border border-[#1F4D2E]/15 rounded-md px-4 py-2.5 mb-6 focus:outline-none focus:ring-2 focus:ring-[#C1272D]" placeholder="Choose a password" />
            <button disabled={busy} data-testid="signup-submit" className="w-full bg-[#C1272D] text-white rounded-full px-4 py-3 font-semibold hover:bg-[#9E1F24] transition-colors inline-flex items-center justify-center gap-2">{busy ? <Loader2 className="animate-spin" size={18} /> : "Create account"}</button>
          </form>
          <p className="text-sm text-[#4F6B54] mt-6 text-center">Already have an account? <Link to="/login" className="text-[#C1272D] font-semibold hover:underline" data-testid="to-login">Sign in</Link></p>
        </div>
        <Link to="/" className="block text-center text-sm text-[#4F6B54] mt-6 hover:text-[#1F4D2E]">← Back to site</Link>
      </div>
    </div>
  );
}
